import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './authTokens';
import { clearUserSession, setUserSession } from './authSession';
import { clearRegularExamStartRecords } from './regularExamStartRecord';
import type { ExamQuestionAnswerPayloadMap } from '../pages/examportal/utils/examAnswerPayload';
import type { SampleExamApiQuestion } from './sampleExamTypes';
import AnswerOfflineStorageService from './AnswerOfflineStorageService';
import { emitSessionConflict } from './sessionConflictEvent';

const JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
} as const;

function normalizeExamQuestionAnswerMap(
  questionAnswer: ExamQuestionAnswerPayloadMap
): ExamQuestionAnswerPayloadMap {
  const normalized: ExamQuestionAnswerPayloadMap = {};
  for (const [qid, entry] of Object.entries(questionAnswer)) {
    const key = String(qid);
    if ('answer' in entry) {
      normalized[key] = { answer: String(entry.answer) };
    } else if ('matchPairAnswer' in entry && entry.matchPairAnswer) {
      const pairs: Record<string, string> = {};
      for (const [left, right] of Object.entries(entry.matchPairAnswer)) {
        pairs[left] = String(right);
      }
      normalized[key] = { matchPairAnswer: pairs };
    }
  }
  return normalized;
}

type LoginData = {
  accessToken: string;
  refreshToken: string;
  registrationId?: number;
  name?: string;
  mname?: string;
  rollno?: string;
  practicalExamCenter?: string;
  startTime?: string;
  endTime?: string;
  [key: string]: unknown;
};

type LoginResponse = {
  data: LoginData;
  [key: string]: unknown;
};

export type ExamPaper = {
  paperId: number;
  name: string;
  paperType: string;
  totalMarks: number;
  sessionStatus: string;
  sessionId?: number;
  submittedTime?: string;
  remainingTimeToStart?: number;
  remainingTimeToEnd?: number;
};

export type ExamItem = {
  examId: number;
  name: string;
  examType: string;
  durationMinutes?: number;
  remainingTimeToStart?: number;
  remainingTimeToEnd?: number;
  status?: string;
  endTime?: string;
  papers: ExamPaper[];
};

type ExamsResponse = {
  data: ExamItem[];
  [key: string]: unknown;
};

function parseSessionIdFromExamStartResponse(data: unknown): number | null {
  if (data == null) return null;
  if (typeof data === 'number' && Number.isFinite(data)) return data;
  if (typeof data === 'string') {
    const n = Number(data);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof data !== 'object') return null;
  const root = data as Record<string, unknown>;
  const inner = root.data;
  if (typeof inner === 'number' && Number.isFinite(inner)) return inner;
  if (typeof inner === 'string') {
    const n = Number(inner);
    return Number.isFinite(n) ? n : null;
  }
  const raw =
    inner != null && typeof inner === 'object' && inner !== null && 'sessionId' in inner
      ? (inner as Record<string, unknown>).sessionId
      : root.sessionId;
  if (raw == null) return null;
  const n = Number(raw as string | number);
  return Number.isFinite(n) ? n : null;
}

function unwrapQuestionListPayload(data: unknown): SampleExamApiQuestion[] {
  if (Array.isArray(data)) return data as SampleExamApiQuestion[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: SampleExamApiQuestion[] }).data;
  }
  return [];
}

function getBaseUrl(): string {
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  if (!baseUrl) throw new Error('API base URL is not defined in the environment variables.');
  return baseUrl;
}

// Auth-free client (used for login/refresh to avoid interceptor loops)
const authClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Main client (adds auth + refreshes on 401 once)
const client: AxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  if (!accessToken || !refreshToken) {
    clearTokens();
    throw new Error('Missing tokens');
  }

  const res = await authClient.post<{ accessToken?: string; data?: { accessToken?: string } }>(
    'vidyarthi/refresh',
    { refreshToken },
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

  const newAccessToken = res.data?.accessToken ?? res.data?.data?.accessToken;
  if (!newAccessToken) {
    clearTokens();
    throw new Error('Failed to refresh access token');
  }

  // Keep refresh token as-is unless API also returns a new one
  setTokens({ accessToken: newAccessToken, refreshToken });
  return newAccessToken;
}

client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalConfig = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;

    // Only retry once on 401, and never for login/refresh endpoints
    const url = (originalConfig?.url ?? '').toString();
    const isAuthEndpoint = url.includes('vidyarthi/login') || url.includes('vidyarthi/refresh');

    if (status !== 401 || !originalConfig || originalConfig._retry || isAuthEndpoint) {
      // If refresh itself returned 401, the session is invalid on the server side
      // (e.g. user logged in from another device). Notify the UI.
      if (status === 401 && isAuthEndpoint) {
        emitSessionConflict();
      }
      return Promise.reject(error);
    }

    originalConfig._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }
      const newToken = await refreshPromise;
      originalConfig.headers = originalConfig.headers ?? {};
      (originalConfig.headers as any).Authorization = `Bearer ${newToken}`;
      // Retry the original request; if this also 401s, the interceptor runs again
      // but _retry is true so it falls into the branch above and emits the event.
      return client.request(originalConfig);
    } catch (e) {
      // Refresh call itself failed — could be 401 (kicked out) or network error
      const axiosErr = e as AxiosError;
      if (!axiosErr.response || axiosErr.response.status === 401) {
        emitSessionConflict();
      }
      clearTokens();
      return Promise.reject(e);
    }
  }
);

const apiService = {
  client,

  async login(identifier: string, password: string): Promise<LoginData> {
    const res = await authClient.post<LoginResponse>('vidyarthi/login', {
      identifier,
      password,
    });
    const payload = res.data?.data;
    if (!payload?.accessToken || !payload?.refreshToken) {
      throw new Error('Login response missing tokens');
    }
    setTokens({ accessToken: payload.accessToken, refreshToken: payload.refreshToken });
    setUserSession(
      payload.name,
      payload.registrationId,
      payload.practicalExamCenter,
      payload.mname,
      payload.startTime,
      payload.endTime,
      payload.rollno
    );
    return payload;
  },

  async getPracticalCenters(): Promise<string[]> {
    const res = await client.get<{ data: string[] }>('vidyarthi/practical-centers');
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async savePracticalCenter(center: string): Promise<void> {
    await client.post('vidyarthi/practical-center', { center });
  },

  async getAvailableExams(): Promise<ExamItem[]> {
    const res = await client.get<ExamsResponse>('vidyarthi/exams');
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async getSampleExamQuestions(paperId: number): Promise<SampleExamApiQuestion[]> {
    const res = await client.get<{ data: SampleExamApiQuestion[] }>(
      'vidyarthi/sample-exam/questions',
      {
        params: { paperId },
      }
    );
    return unwrapQuestionListPayload(res.data);
  },

  /**
   * Start a regular exam (call from dashboard when user confirms Start Exam and the paper has no session yet).
   * POST `vidyarthi/exam/start?paperId=…`.
   */
  async startRegularExam(paperId: number): Promise<number | null> {
    const res = await client.request<unknown>({
      method: 'POST',
      url: 'vidyarthi/exam/start',
      params: { paperId },
    });
    return parseSessionIdFromExamStartResponse(res.data);
  },

  /** Regular exam: GET vidyarthi/exam/questions?paperId=… (use when examType is REGULAR from /vidyarthi/exams). */
  async getRegularExamQuestions(paperId: number): Promise<{
    questions: SampleExamApiQuestion[];
    remainingTimeToEnd?: number;
  }> {
    const res = await client.get<any>('vidyarthi/exam/questions', {
      params: { paperId },
    });
    const questions = unwrapQuestionListPayload(res.data);
    const dataRoot = res.data?.data;
    const remainingTimeToEnd =
      res.data?.remainingTimeToEnd ??
      (typeof dataRoot === 'object' ? dataRoot?.remainingTimeToEnd : undefined);

    return {
      questions,
      remainingTimeToEnd: remainingTimeToEnd != null ? Number(remainingTimeToEnd) : undefined,
    };
  },

  /**
   * POST `vidyarthi/exam/answer` — body:
   * `{ "sessionId": 6, "questionAnswer": { "471": { "matchPairAnswer": { ... } }, "473": { "answer": "true" } } }`
   */
  async postExamAnswer(
    sessionId: number,
    questionAnswer: ExamQuestionAnswerPayloadMap
  ): Promise<string[]> {
    const sid = Number(sessionId);
    if (!Number.isFinite(sid)) {
      throw new Error('Invalid sessionId for exam answer');
    }

    // Merge with any pending items from localStorage
    const pending = AnswerOfflineStorageService.getPendingAnswers(sid);
    const pendingPayload: ExamQuestionAnswerPayloadMap = {};
    for (const [qid, data] of Object.entries(pending)) {
      pendingPayload[qid] = data.entry;
    }

    const combined = { ...pendingPayload, ...questionAnswer };
    const normalized = normalizeExamQuestionAnswerMap(combined);
    if (Object.keys(normalized).length === 0) {
      return [];
    }

    try {
      await client.post(
        'vidyarthi/exam/answer',
        { sessionId: sid, questionAnswer: normalized },
        { headers: JSON_HEADERS }
      );
      // Success: Clear the successfully sent answers from offline storage
      const syncedIds = Object.keys(normalized);
      AnswerOfflineStorageService.clearPendingAnswers(sid, syncedIds);
      return syncedIds;
    } catch (err: any) {
      // If it's a network error (no response) or specific network codes, save to local storage
      const isNetworkError =
        !err.response ||
        err.code === 'ERR_NETWORK' ||
        err.code === 'ECONNABORTED' ||
        err.message === 'Network Error';

      if (isNetworkError) {
        for (const [qid, entry] of Object.entries(normalized)) {
          AnswerOfflineStorageService.saveAnswer(sid, qid, entry);
        }
      }
      throw err;
    }
  },

  /** Explicitly flushes any pending answers in localStorage to the server. */
  async syncOfflineAnswers(sessionId: number): Promise<string[]> {
    const sid = Number(sessionId);
    if (!Number.isFinite(sid) || !AnswerOfflineStorageService.hasPendingAnswers(sid)) {
      return [];
    }
    // Attempting a sync is basically calling postExamAnswer with no new answers,
    // which triggers the "pull pending and send" logic.
    return this.postExamAnswer(sid, {});
  },

  /**
   * POST `vidyarthi/exam/submit` — same body shape as exam/answer, with all answers collected so far.
   */
  async submitRegularExam(
    sessionId: number,
    questionAnswer: ExamQuestionAnswerPayloadMap
  ): Promise<void> {
    const sid = Number(sessionId);
    if (!Number.isFinite(sid)) {
      throw new Error('Invalid sessionId for exam submit');
    }

    const normalized = normalizeExamQuestionAnswerMap(questionAnswer);

    await client.post(
      'vidyarthi/exam/submit',
      { sessionId: sid, questionAnswer: normalized },
      { headers: JSON_HEADERS }
    );
  },

  /** Binary image for a sample exam question (use when API returns imagePath on the question). */
  async getQuestionImageBlob(questionId: number): Promise<Blob> {
    const res = await client.get(`vidyarthi/image/questionid/${questionId}`, {
      responseType: 'blob',
      headers: {
        Accept: 'image/*,*/*',
      },
    });
    return res.data as Blob;
  },

  logout(): void {
    clearTokens();
    clearUserSession();
    clearRegularExamStartRecords();
  },

  // Backwards-compatible request wrapper used across the app
  async request<T>(
    method: 'get' | 'post' | 'put' | 'delete',
    suburl: string,
    data: any = null,
    config: AxiosRequestConfig = {},
    onSuccess: (response: T) => void,
    onFailure: (error: AxiosError) => void
  ): Promise<void> {
    try {
      let response: AxiosResponse<T>;
      switch (method) {
        case 'get':
          response = await client.get<T>(suburl, config);
          break;
        case 'post':
          response = await client.post<T>(suburl, data, config);
          break;
        case 'put':
          response = await client.put<T>(suburl, data, config);
          break;
        case 'delete':
          response = await client.delete<T>(suburl, config);
          break;
        default:
          throw new Error('Invalid method');
      }
      onSuccess(response.data);
    } catch (err) {
      if (axios.isAxiosError(err)) onFailure(err);
      else onFailure(new Error('Unexpected error occurred') as AxiosError);
    }
  },
};

export default apiService;
